// ENG-4123: a pending phone login has to survive a full page load.
//
// A renter leaves the page for the SMS app and comes back, which on a phone is
// usually a reload — and a reload drops in-memory state, so the code-entry step
// became unreachable and the code already sent could not be entered.
//
// sessionStorage is deliberate: it is per tab, so it survives a reload but dies
// with the tab and never leaves a phone number behind on a shared machine.

export const PENDING_PHONE_LOGIN_STORAGE_KEY = 'auth.pendingPhoneLogin';

export interface PendingPhoneLogin {
  phone: string;
}

/** The phone a code was last sent to, or null when no login is pending. */
export function readPendingPhoneLogin(): PendingPhoneLogin | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_PHONE_LOGIN_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { phone?: unknown } | null;
    const phone = typeof parsed?.phone === 'string' ? parsed.phone.trim() : '';
    return phone ? { phone } : null;
  } catch {
    // Storage unavailable (private mode / quota=0) or a malformed value: the
    // flow just falls back to a single page load.
    return null;
  }
}

/** Remember the phone a code was sent to, so a reload can reach the code form. */
export function writePendingPhoneLogin(phone: string): void {
  const trimmed = (phone || '').trim();
  if (!trimmed) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      PENDING_PHONE_LOGIN_STORAGE_KEY,
      JSON.stringify({ phone: trimmed })
    );
  } catch {
    // Best effort: the flow still works within a single page load.
  }
}

/** Drop the pending login: Back, or a code the server has already consumed. */
export function clearPendingPhoneLogin(): void {
  try {
    window.sessionStorage.removeItem(PENDING_PHONE_LOGIN_STORAGE_KEY);
  } catch {
    // Best effort.
  }
}
